import React from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Copy, Share2, MessageCircle, Mail, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { shareLink, copyText, whatsappHref, emailHref, smsHref } from '../lib/share';

/**
 * Invite one person: share sheet, copy, WhatsApp, e-mail or SMS with a ready message + link.
 * Used for group invites (link /groups/join/CODE) and for inviting a friend to the app (referral link).
 */
export function InviteDialog({ open, onOpenChange, title, description, text, link }) {
  const { t } = useTranslation();

  const onShare = async () => {
    const res = await shareLink({ title: 'SocraQuest', text, url: link });
    if (res === 'copied') toast.success(t('invite.copied'));
    else if (res === 'failed') toast.info(link);
  };

  const onCopy = async () => {
    if (await copyText(`${text}\n${link}`)) toast.success(t('invite.copied'));
    else toast.info(link); // clipboard blocked: show the link so it can be copied by hand
  };

  const linkBtn = 'flex items-center justify-center gap-1.5 rounded-xl border border-slate-600 bg-slate-900/40 text-slate-200 hover:bg-slate-700 hover:text-white h-11 text-sm font-medium';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white w-[calc(100vw-1.5rem)] max-w-md rounded-2xl" data-testid="invite-dialog">
        <DialogHeader className="min-w-0">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription className="text-slate-400">{description}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-3 pt-2 min-w-0">
          <div className="flex gap-2 min-w-0">
            <code className="flex-1 min-w-0 text-xs text-slate-300 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2.5 overflow-x-auto whitespace-nowrap" data-testid="invite-link">
              {link}
            </code>
            <Button type="button" onClick={onCopy} variant="outline" data-testid="invite-copy-btn"
              className="bg-transparent border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white rounded-lg h-10 flex-shrink-0">
              <Copy className="w-4 h-4 mr-1.5" /> {t('invite.copy')}
            </Button>
          </div>

          <Button type="button" onClick={onShare} data-testid="invite-share-btn"
            className="w-full h-11 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 font-semibold">
            <Share2 className="w-4 h-4 mr-2" /> {t('invite.share')}
          </Button>

          <div className="grid grid-cols-3 gap-2">
            <a href={whatsappHref(text, link)} target="_blank" rel="noopener noreferrer" className={linkBtn} data-testid="invite-whatsapp">
              <MessageCircle className="w-4 h-4 text-green-400" /> WhatsApp
            </a>
            <a href={emailHref(t('invite.emailSubject'), text, link)} className={linkBtn} data-testid="invite-email">
              <Mail className="w-4 h-4 text-sky-300" /> {t('invite.email')}
            </a>
            <a href={smsHref(text, link)} className={linkBtn} data-testid="invite-sms">
              <MessageSquare className="w-4 h-4 text-amber-300" /> SMS
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
